// 카카오톡 내보내기 텍스트를 브라우저에서만 파싱한다. 서버로 보내지 않는다.
export const EVIDENCE_LOW = 2;
export const EVIDENCE_MAX_CHARS = 100000;
export const EVIDENCE_DRAFT_KEY = "lawlow.evidenceDraft";
export const EVIDENCE_RETURN_KEY = "lawlow.evidenceReturn";
export const EVIDENCE_INPUT_KEY = "lawlow.evidenceInput";
const INPUT_MAX_AGE = 30 * 60 * 1000;

const ANDROID = /^(\d{4})년 (\d{1,2})월 (\d{1,2})일 (오전|오후) (\d{1,2}):(\d{2}), (.+?) : ([\s\S]*)$/;
const IOS = /^(\d{4})\. ?(\d{1,2})\. ?(\d{1,2})\. (오전|오후) (\d{1,2}):(\d{2}), (.+?) : ([\s\S]*)$/;
const PC_DATE = /^-{3,} ?(\d{4})년 (\d{1,2})월 (\d{1,2})일 .*? ?-{3,}$/;
const PC_LINE = /^\[(.+?)\] \[(오전|오후) (\d{1,2}):(\d{2})\] ([\s\S]*)$/;
// 대화가 아닌 줄. 본문에 붙이지 않고 버린다.
const NOISE = /^(사진( \d+장)?|이모티콘|동영상|음성메시지|삭제된 메시지입니다\.?|.*님이 .*(입장|퇴장|초대)하였습니다\.?|저장한 날짜 ?:.*|.*님과 카카오톡 대화)$/;

function at(year, month, day, half, hour, minute) {
  const h = Number(hour) % 12 + (half === "오후" ? 12 : 0);
  return new Date(Number(year), Number(month) - 1, Number(day), h, Number(minute));
}

// 포맷은 첫 매칭 줄로 정한다. 셋 다 아니면 null.
function detect(lines) {
  for (const line of lines) {
    if (ANDROID.test(line)) return "android";
    if (IOS.test(line)) return "ios";
    if (PC_DATE.test(line)) return "pc";
  }
  return null;
}

export function parseKakaoExport(text) {
  const lines = String(text || "").split(/\r?\n/);
  const format = detect(lines);
  if (!format) return { format: null, messages: [] };

  const messages = [];
  let day = null;
  for (const line of lines) {
    const pattern = format === "android" ? ANDROID : format === "ios" ? IOS : null;
    const match = pattern && line.match(pattern);
    if (match) {
      const [, year, month, date, half, hour, minute, sender, body] = match;
      push(messages, at(year, month, date, half, hour, minute), sender, body);
      continue;
    }
    if (format === "pc") {
      const header = line.match(PC_DATE);
      if (header) { day = header.slice(1, 4); continue; }
      const message = line.match(PC_LINE);
      if (message && day) {
        const [, sender, half, hour, minute, body] = message;
        push(messages, at(day[0], day[1], day[2], half, hour, minute), sender, body);
        continue;
      }
    }
    // 새 메시지가 아니면 직전 메시지의 줄바꿈으로 본다.
    const last = messages[messages.length - 1];
    if (last && line.trim()) last.text = `${last.text}\n${line}`.trim();
  }
  return { format, messages: messages.filter((m) => m.text && !NOISE.test(m.text)) };
}

function push(messages, when, sender, body) {
  messages.push({ at: when.getTime(), sender: sender.trim(), text: body.trim() });
}

// 파싱만으로 확실한 값만 낸다. 어떤 말이 증거인지는 서버 분석이 판단한다.
export function summarize(messages) {
  return {
    total: messages.length,
    from: messages.length ? messages[0].at : null,
    to: messages.length ? messages[messages.length - 1].at : null,
    senders: [...new Set(messages.map((m) => m.sender))],
  };
}

export function sliceByRange(messages, from, to) {
  return messages.filter((m) => (from == null || m.at >= from) && (to == null || m.at <= to));
}

export function serialize(messages) {
  return messages.map((m) => `${formatAt(m.at)} ${m.sender}: ${m.text}`).join("\n");
}

export function formatAt(value) {
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}. ${pad(d.getMonth() + 1)}. ${pad(d.getDate())}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDay(value) {
  const d = new Date(value);
  return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, "0")}.`;
}

export function readEvidenceDraft() {
  try {
    const draft = JSON.parse(sessionStorage.getItem(EVIDENCE_DRAFT_KEY));
    const id = draft?.request?.request_id;
    if (typeof id !== "string" || !/^[a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(id)) return null;
    return { request: { request_id: id } };
  } catch (_) { return null; }
}

export function readEvidenceInput() {
  try {
    const input = JSON.parse(sessionStorage.getItem(EVIDENCE_INPUT_KEY));
    const age = Date.now() - input?.savedAt;
    if (!input || typeof input.raw !== "string" || !input.raw.trim() ||
        !Number.isFinite(age) || age < 0 || age >= INPUT_MAX_AGE ||
        (input.range !== null && (!Number.isFinite(input.range?.from) ||
          !Number.isFinite(input.range?.to) || input.range.from > input.range.to))) {
      clearEvidenceInput();
      return null;
    }
    if (!input.request?.request_id || typeof input.request.transcript !== "string") input.request = null;
    return input;
  } catch (_) { clearEvidenceInput(); return null; }
}

export function saveEvidenceInput({ raw, range, request }) {
  try {
    sessionStorage.setItem(EVIDENCE_INPUT_KEY, JSON.stringify({ raw, range, request, savedAt: Date.now() }));
    return true;
  } catch (_) { clearEvidenceInput(); return false; }
}

export function clearEvidenceInput() {
  try {
    sessionStorage.removeItem(EVIDENCE_INPUT_KEY);
    sessionStorage.removeItem(EVIDENCE_RETURN_KEY);
  }
  catch (_) { /* Keep the current input on screen when storage is unavailable. */ }
}

export function clearEvidenceDraft() {
  clearEvidenceInput();
  try {
    sessionStorage.removeItem(EVIDENCE_DRAFT_KEY);
  } catch (_) { /* Keep the current input on screen when storage is unavailable. */ }
}

export function consumeEvidenceReturn(pathname, loggedIn) {
  if (!loggedIn || !["/main", "/app-guide"].includes(pathname)) return false;
  try {
    const value = sessionStorage.getItem(EVIDENCE_RETURN_KEY);
    if (!value) return false;
    sessionStorage.removeItem(EVIDENCE_RETURN_KEY);
    const age = Date.now() - Number(value);
    return age >= 0 && age < INPUT_MAX_AGE && Boolean(readEvidenceInput());
  } catch (_) { return false; }
}
